import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { BillingCycle } from "../api/hooks/useTradingStrategies";

export interface StrategyCartItem {
  strategyId: string;
  name: string;
  // The cycle the customer chose when adding to cart and the actual price
  // they'll pay for that cycle (per month for Monthly, per year for Yearly).
  // Storing the resolved price keeps the cart total math trivial and
  // independent of catalogue price changes after the item was added.
  billingCycle: BillingCycle;
  priceUsd: number;
  // The offer the customer chose for this line, or null for none. One per line: Stripe applies a
  // coupon per subscription item, so this is the shape the billing takes anyway.
  offerId?: string | null;
}

/** One of our membership tiers. Offers don't apply to memberships yet, so a line has none. */
export interface MembershipCartItem {
  tierId: string;
  name: string;
  billingCycle: BillingCycle;
  // As on a StrategyCartItem: the price for the chosen cycle when it was added.
  priceUsd: number;
}

/** An add-on. Offers don't apply to add-ons, as to memberships, so a line has none. */
export interface AddOnCartItem {
  addOnId: string;
  name: string;
  billingCycle: BillingCycle;
  // As on a StrategyCartItem: the price for the chosen cycle when it was added.
  priceUsd: number;
}

/** A monthly subscription the customer holds, going to yearly. */
export interface UpgradeLine {
  strategyId: string;
  name: string;
  // The yearly list price when it was added, for the same reason a StrategyCartItem keeps its price.
  yearlyPriceUsd: number;
  // Chosen when it goes in the cart, so there is no undecided state as on a StrategyCartItem. Null for none.
  offerId: string | null;
}

/** A yearly subscription the customer holds, restarting its year today. */
export interface ExtendLine {
  strategyId: string;
  name: string;
  // What the line bills at, for the savings the offer picker names. The cart's own figures are Stripe's.
  yearlyPriceUsd: number;
  // Chosen when it goes in the cart, as on an upgrade. Null for none.
  offerId: string | null;
}

interface CartState {
  strategyItems: StrategyCartItem[];
  membershipItems: MembershipCartItem[];
  addOnItems: AddOnCartItem[];
  // An upgrade or an extension is checked out on its own, so while either is set the rest of the cart
  // is empty. The pages ask before emptying anything; the store only makes sure they never mix.
  upgrade: UpgradeLine | null;
  extension: ExtendLine | null;
  // Set while a Stripe payment session is open on the checkout page. That
  // session is priced against a fixed set of lines and cannot follow later
  // edits, so the cart is frozen for as long as it's open — otherwise a
  // customer can remove an item and still be charged the original amount.
  // Enforced here rather than by hiding buttons so no edit surface, present
  // or future, can move the cart out from under an open payment.
  checkoutLocked: boolean;
  addStrategy: (item: StrategyCartItem) => void;
  removeStrategy: (strategyId: string) => void;
  addMembership: (item: MembershipCartItem) => void;
  removeMembership: (tierId: string) => void;
  updateMembershipBillingCycle: (tierId: string, billingCycle: BillingCycle, priceUsd: number) => void;
  addAddOn: (item: AddOnCartItem) => void;
  removeAddOn: (addOnId: string) => void;
  updateAddOnBillingCycle: (addOnId: string, billingCycle: BillingCycle, priceUsd: number) => void;
  updateStrategyBillingCycle: (
    strategyId: string,
    billingCycle: BillingCycle,
    priceUsd: number
  ) => void;
  /**
   * Puts an offer on a line, or takes it off with null.
   *
   * `freeFrom` are the lines the offer should come off first, because it can only be used on so
   * many strategies. Which those are is the caller's choice — it is the only place that knows what
   * each line would save — but `maxStrategies` is enforced here regardless, because the caller
   * decides against a render and this runs against the cart as it actually is.
   */
  setLineOffer: (
    strategyId: string,
    offerId: string | null,
    freeFrom?: string[],
    maxStrategies?: number | null
  ) => void;
  /**
   * Records which lines take an offer nobody had to choose, for the whole cart in one write.
   *
   * A line cannot decide this for itself: an offer that can only be used on so many strategies has
   * to land where it is worth most, and that needs every line at once. Lines deciding separately
   * also each saw the offer unused, so a one-strategy offer landed on all of them.
   */
  applyAutoOffers: (decisions: Record<string, string | null>) => void;
  /** Empties the cart and puts the upgrade in it. */
  startUpgrade: (line: UpgradeLine) => void;
  removeUpgrade: () => void;
  setUpgradeOffer: (offerId: string | null) => void;
  /** Empties the cart and puts the extension in it. */
  startExtension: (line: ExtendLine) => void;
  removeExtension: () => void;
  setExtensionOffer: (offerId: string | null) => void;
  clear: () => void;
  setCheckoutLocked: (locked: boolean) => void;
}

/**
 * Takes a capped offer back off the lines that push it over its limit, keeping the one it was just
 * put on.
 *
 * Belt and braces over the caller's own `freeFrom`, and the only thing that actually holds. Lines
 * decide for themselves whether to take the single offer that fits them, so several can decide in
 * one render — each looking at a cart where the offer is still unused, each concluding it has room.
 * That put a one-strategy offer on three lines at once, and checkout refuses the whole basket for
 * overspending it. Here there is no stale view: every write sees what the last one did.
 */
function enforceOfferCap(
  items: StrategyCartItem[],
  strategyId: string,
  offerId: string | null,
  maxStrategies: number | null
): StrategyCartItem[] {
  if (offerId === null || maxStrategies === null) {
    return items;
  }

  const using = items.filter((i) => i.offerId === offerId);
  const excess = using.length - maxStrategies;
  if (excess <= 0) {
    return items;
  }

  // Oldest first, and never the line it was just put on.
  const dropping = new Set(
    using
      .filter((i) => i.strategyId !== strategyId)
      .slice(0, excess)
      .map((i) => i.strategyId)
  );

  return items.map((i) => (dropping.has(i.strategyId) ? { ...i, offerId: null } : i));
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      strategyItems: [],
      membershipItems: [],
      addOnItems: [],
      upgrade: null,
      extension: null,
      checkoutLocked: false,
      addStrategy: (item) =>
        set((state) => {
          if (state.checkoutLocked || state.upgrade !== null || state.extension !== null) return state;
          if (state.strategyItems.some((existing) => existing.strategyId === item.strategyId)) {
            return state;
          }
          return { strategyItems: [...state.strategyItems, item] };
        }),
      removeStrategy: (strategyId) =>
        set((state) =>
          state.checkoutLocked
            ? state
            : {
                strategyItems: state.strategyItems.filter(
                  (i) => i.strategyId !== strategyId
                ),
              }
        ),
      addMembership: (item) =>
        set((state) => {
          if (state.checkoutLocked || state.upgrade !== null || state.extension !== null) return state;
          if (state.membershipItems.some((existing) => existing.tierId === item.tierId)) {
            return state;
          }
          return { membershipItems: [...state.membershipItems, item] };
        }),
      removeMembership: (tierId) =>
        set((state) =>
          state.checkoutLocked
            ? state
            : { membershipItems: state.membershipItems.filter((i) => i.tierId !== tierId) }
        ),
      updateMembershipBillingCycle: (tierId, billingCycle, priceUsd) =>
        set((state) =>
          state.checkoutLocked
            ? state
            : {
                membershipItems: state.membershipItems.map((i) =>
                  i.tierId === tierId ? { ...i, billingCycle, priceUsd } : i
                ),
              }
        ),
      addAddOn: (item) =>
        set((state) => {
          if (state.checkoutLocked || state.upgrade !== null || state.extension !== null) return state;
          if (state.addOnItems.some((existing) => existing.addOnId === item.addOnId)) {
            return state;
          }
          return { addOnItems: [...state.addOnItems, item] };
        }),
      removeAddOn: (addOnId) =>
        set((state) =>
          state.checkoutLocked
            ? state
            : { addOnItems: state.addOnItems.filter((i) => i.addOnId !== addOnId) }
        ),
      updateAddOnBillingCycle: (addOnId, billingCycle, priceUsd) =>
        set((state) =>
          state.checkoutLocked
            ? state
            : {
                addOnItems: state.addOnItems.map((i) =>
                  i.addOnId === addOnId ? { ...i, billingCycle, priceUsd } : i
                ),
              }
        ),
      updateStrategyBillingCycle: (strategyId, billingCycle, priceUsd) =>
        set((state) =>
          state.checkoutLocked
            ? state
            : {
                strategyItems: state.strategyItems.map((i) =>
                  i.strategyId === strategyId
                    ? // The offer goes with the old cycle: a monthly offer on a yearly line is a
                      // free year, and checkout refuses it. Left as no offer rather than undecided,
                      // so the customer picks from what fits the new cycle instead of having one
                      // applied on their behalf for something they have just changed.
                      { ...i, billingCycle, priceUsd, offerId: null }
                    : i
                ),
              }
        ),
      setLineOffer: (strategyId, offerId, freeFrom = [], maxStrategies = null) =>
        set((state) => {
          if (state.checkoutLocked) return state;

          const items = state.strategyItems.map((i) => {
            if (i.strategyId === strategyId) {
              return { ...i, offerId };
            }

            return freeFrom.includes(i.strategyId) ? { ...i, offerId: null } : i;
          });

          return { strategyItems: enforceOfferCap(items, strategyId, offerId, maxStrategies) };
        }),
      applyAutoOffers: (decisions) =>
        set((state) =>
          state.checkoutLocked
            ? state
            : {
                strategyItems: state.strategyItems.map((i) =>
                  i.strategyId in decisions ? { ...i, offerId: decisions[i.strategyId] } : i
                ),
              }
        ),
      startUpgrade: (line) =>
        set((state) =>
          state.checkoutLocked
            ? state
            : { strategyItems: [], membershipItems: [], addOnItems: [], upgrade: line, extension: null }
        ),
      removeUpgrade: () =>
        set((state) => (state.checkoutLocked ? state : { upgrade: null })),
      setUpgradeOffer: (offerId) =>
        set((state) =>
          state.checkoutLocked || state.upgrade === null
            ? state
            : { upgrade: { ...state.upgrade, offerId } }
        ),
      startExtension: (line) =>
        set((state) =>
          state.checkoutLocked
            ? state
            : { strategyItems: [], membershipItems: [], addOnItems: [], upgrade: null, extension: line }
        ),
      removeExtension: () =>
        set((state) => (state.checkoutLocked ? state : { extension: null })),
      setExtensionOffer: (offerId) =>
        set((state) =>
          state.checkoutLocked || state.extension === null
            ? state
            : { extension: { ...state.extension, offerId } }
        ),
      // Deliberately not gated by the lock: this is our own post-payment
      // cleanup, not a customer edit, and it runs while the lock is still on.
      clear: () => set({ strategyItems: [], membershipItems: [], addOnItems: [], upgrade: null, extension: null }),
      setCheckoutLocked: (locked) => set({ checkoutLocked: locked }),
    }),
    {
      // Bumped whenever the persisted shape changes, so an old cart comes back
      // empty rather than tripping a runtime error.
      name: "scryer-fo-cart-v4",
      storage: createJSONStorage(() => sessionStorage),
      // Only the cart itself survives a reload. The lock tracks a session that
      // lives on the checkout page, so persisting it could strand a customer
      // with a permanently read-only cart.
      partialize: (state) => ({
        strategyItems: state.strategyItems,
        membershipItems: state.membershipItems,
        addOnItems: state.addOnItems,
        upgrade: state.upgrade,
        extension: state.extension,
      }),
    }
  )
);
